import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {isSnapshotPeriodIncomplete, snapshotHasPublishedDays, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    formatNumberItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    SortableHeader,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";

const ProductMode = {
    Turnover: "turnover",
    Amount: "amount",
};

export function productSummaryKpiValue(summary, mode, productPublication = undefined) {
    if (!snapshotHasPublishedDays(productPublication)) {
        return null;
    }

    if (mode === ProductMode.Turnover) {
        const turnover = summary?.turnover;
        if (turnover?.amount == null || !Number.isFinite(Number(turnover.amount))) {
            return null;
        }

        return turnover;
    }

    const amount = summary?.totalValue;
    return amount == null || !Number.isFinite(Number(amount)) ? null : Number(amount);
}

export function productTablePresentation({loading, totalItems, search, productPublication}) {
    const publicationUnavailable = !snapshotHasPublishedDays(productPublication);

    return {
        showPagination: !loading && !publicationUnavailable,
        emptyState: !loading && publicationUnavailable
            ? "unavailable"
            : !loading && totalItems === 0 ? (search ? "search" : "period") : null,
    };
}

function ProductModeToggle({mode, setMode, t}) {
    return <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {[
            {id: ProductMode.Turnover, label: t("reporting_lab.products.by_revenue", {defaultValue: "Podle obratu"})},
            {id: ProductMode.Amount, label: t("reporting_lab.products.by_quantity", {defaultValue: "Podle kusů"})},
        ].map((item) => <button
            key={item.id}
            type="button"
            onClick={() => setMode(item.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${mode === item.id ? "bg-violet-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"}`}
        >
            {item.label}
        </button>)}
    </div>;
}

export default function ReportingProductsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [mode, setMode] = useState(ProductMode.Turnover);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const [debouncedTableSearch, setDebouncedTableSearch] = useState("");
    const [tableSort, setTableSort] = useState({key: "turnover", direction: "desc"});

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const productPerformanceFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        limit: tablePageSize,
        page: tablePage,
        search: debouncedTableSearch || null,
        orderBy: tableSort.key,
        orderDirection: tableSort.direction,
        mode: "net",
        sortBy: mode,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, tablePageSize, tablePage, debouncedTableSearch, tableSort, mode]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [tableSearch]);

    useEffect(() => {
        setTablePage(1);
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());
        api.productAssortmentPerformance({
                projectId: project.id,
                productAssortmentPerformanceFilter: productPerformanceFilter,
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, productPerformanceFilter]);

    const productPublication = data?.productPublication;
    const productPublicationIncomplete = isSnapshotPeriodIncomplete(productPublication);
    const productSummaryUnavailable = !snapshotHasPublishedDays(productPublication);
    const visibleProducts = snapshotRowsForPublishedDays(data?.products ?? [], productPublication);
    const topProducts = productSummaryUnavailable ? [] : data?.topProducts ?? [];
    const chartProducts = topProducts.slice(0, 10);
    const summary = data?.summary ?? {};
    const totalItems = Number(data?.totalItems ?? 0);
    const sourceTotalItems = summary.sourceTotalItems == null ? null : Number(summary.sourceTotalItems);
    const turnoverValue = productSummaryKpiValue(summary, ProductMode.Turnover, productPublication);
    const quantityValue = productSummaryKpiValue(summary, ProductMode.Amount, productPublication);
    const safeTablePage = Math.min(tablePage, Math.max(1, Number(data?.totalPages ?? 1)));
    const tablePresentation = productTablePresentation({loading, totalItems, search: debouncedTableSearch, productPublication});
    const currency = turnoverValue?.currency ?? [...topProducts, ...visibleProducts].find((product) => product.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const leadingProduct = productSummaryUnavailable ? null : topProducts[0];
    const toggleSort = (key) => {
        setTablePage(1);
        setTableSort((current) => ({
            key,
            direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
        }));
    };
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const topProductsOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, mode === ProductMode.Turnover
                ? (value) => formatChartCurrency(value, currency)
                : formatChartNumber),
        },
        xAxis: {
            type: "value",
            axisLabel: {
                fontSize: 12,
                formatter: formatChartNumber,
            },
        },
        yAxis: {
            type: "category",
            data: [...chartProducts].reverse().map((product) => product.productName),
            axisLabel: {
                fontSize: 12,
                width: 220,
                overflow: "truncate",
            },
        },
        series: [
            {
                name: mode === ProductMode.Turnover ? t("reporting_lab.series.revenue", {defaultValue: "Obrat"}) : t("reporting_lab.products.quantity", {defaultValue: "Kusy"}),
                type: "bar",
                data: [...chartProducts].reverse().map((product, index) => ({
                    value: mode === ProductMode.Turnover ? moneyToMajor(product.turnover) : Number(product.amount ?? 0),
                    itemStyle: {color: ChannelMixColors[(chartProducts.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
            },
        ],
    };

    const productShareOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => mode === ProductMode.Turnover
                ? formatCurrencyItemTooltip(params, currency)
                : formatNumberItemTooltip(params),
        },
        legend: {
            bottom: 0,
            left: "center",
            width: "94%",
            formatter: (name) => name.length > 32 ? `${name.slice(0, 31)}…` : name,
            ...chartLegendStyle,
        },
        series: [
            {
                type: "pie",
                radius: ["36%", "56%"],
                center: ["50%", "36%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => formatChartPercent(params.percent),
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: chartProducts.slice(0, 5).map((product) => ({
                    name: product.productName,
                    value: mode === ProductMode.Turnover ? moneyToMajor(product.turnover) : Number(product.amount ?? 0),
                })),
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.products", {defaultValue: "Produkty"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.products.title", {defaultValue: "K čemu slouží produktový přehled"})}
                    actions={<ProductModeToggle mode={mode} setMode={(nextMode) => {
                        setMode(nextMode);
                        setTablePage(1);
                        setTableSort({key: nextMode, direction: "desc"});
                    }} t={t}/>}
                >
                    {t("reporting_lab.descriptions.products.text", {defaultValue: "Produktové tržby a prodané kusy vycházejí z dokončených objednávek. Přehled ukazuje nejsilnější produkty, jejich podíl a detailní tabulku pro rozhodování o sortimentu."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={productPublication}/>

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.products.tracked_products", {defaultValue: "Sledované produkty"})} value={productSummaryUnavailable ? "—" : renderNumber(sourceTotalItems)} meta={productPublicationIncomplete ? snapshotUnavailableShort(productPublication, t) : t("reporting_lab.products.entire_selection", {defaultValue: "Celý vybraný sortiment"})}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard
                        loading={loading}
                        label={mode === ProductMode.Turnover
                            ? t("reporting_lab.products.total_revenue", {defaultValue: "Tržby po slevách"})
                            : t("reporting_lab.products.total_quantity", {defaultValue: "Celkem prodaných kusů"})}
                        value={(mode === ProductMode.Turnover ? turnoverValue : quantityValue) === null
                            ? "—"
                            : mode === ProductMode.Turnover ? renderCurrencyValue(turnoverValue, true) : renderNumber(quantityValue)}
                        meta={t("reporting_lab.products.completed_orders_basis", {defaultValue: "Dokončené objednávky"})}
                        info={mode === ProductMode.Turnover
                            ? t("reporting_lab.products.total_revenue_help", {defaultValue: "Zahrnuje celý vybraný sortiment, řádkové slevy a poměrně rozdělené doložené objednávkové slevy. Datixo je rozděluje mezi kladné produktové řádky se stejnou měnou a sazbou DPH; jde o analytické rozdělení, nikoli o přiřazení slevy konkrétním produktům potvrzené e-shopem. Neurčené úpravy zůstávají nepřiřazené."})
                            : undefined}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                    />
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading}
                        label={t("reporting_lab.products.best_product", {defaultValue: "Nejsilnější produkt"})}
                        value={productSummaryUnavailable ? "—" : leadingProduct ? (mode === ProductMode.Turnover ? renderCurrencyValue(leadingProduct.turnover, true) : renderNumber(leadingProduct.amount)) : "-"}
                        meta={<span className="block truncate">{leadingProduct?.productName}</span>}
                        metaTooltip={leadingProduct?.productName ?? null}
                        metaTone="entity"
                        emphasizeMeta={false}
                    />
                </div>

                {!productSummaryUnavailable && <Panel loading={loading} title={t("reporting_lab.products.top_products", {defaultValue: "Top produkty"})} className="col-span-12 xl:col-span-8">
                    <EChart option={topProductsOption} className="h-96"/>
                </Panel>}
                {!productSummaryUnavailable && <Panel loading={loading} title={mode === ProductMode.Turnover
                    ? t("reporting_lab.products.revenue_share", {defaultValue: "Rozpad top 5 produktů podle obratu"})
                    : t("reporting_lab.products.quantity_share", {defaultValue: "Rozpad top 5 produktů podle kusů"})} className="col-span-12 xl:col-span-4">
                    <EChart option={productShareOption} className="h-96"/>
                </Panel>}

                <Panel loading={loading} title={t("reporting_lab.products.product_table", {defaultValue: "Produktový výkon"})} className="col-span-12">
                    <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={(productPublicationIncomplete ? totalItems : sourceTotalItems) > 15 || tableSearch !== ""} t={t}/>
                    <div className="overflow-x-auto" aria-busy={loading}>
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <SortableHeader label={t("reporting_lab.products.product", {defaultValue: "Produkt"})} sortKey="product" sort={tableSort} onSort={toggleSort}/>
                                <SortableHeader label={t("reporting_lab.products.quantity", {defaultValue: "Kusy"})} sortKey="amount" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.products.average_unit_price", {defaultValue: "Průměrná cena / kus"})} sortKey="unitPrice" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right"/>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visibleProducts.map((product) => <tr key={`${product.integrationId ?? "integration"}-${product.productRemoteId ?? product.productName}`}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{product.productName}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(product.amount)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{Number(product.amount ?? 0) > 0 ? renderCurrencyValue({amount: moneyAmount(product.turnover) / Number(product.amount), currency}, true) : "-"}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.turnover, true)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {tablePresentation.showPagination && <TablePagination page={safeTablePage} pageSize={tablePageSize} totalItems={totalItems} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>}
                    {tablePresentation.emptyState && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{tablePresentation.emptyState === "unavailable"
                        ? t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"})
                        : tablePresentation.emptyState === "search"
                        ? t("reporting_lab.products.search_empty", {defaultValue: "Vyhledávání neodpovídá žádným produktům."})
                        : t("reporting_lab.products.empty", {defaultValue: "Pro vybrané období nejsou dostupná produktová data."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
